import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import { Store } from '@ngrx/store';
import { AppState, getState } from '../store/models/app.state';

import { AppConfig } from '../core/config/app.config';
import { QuestionModel } from './question.model';
import { HttpClient } from '@angular/common/http';
import { cloneDeep, each, isEqual, omit, reject } from 'lodash-es';
import { IntegrationService } from '../core/integration.service';
import { FormModel } from '../form/form.model';
import { IIntegrationRule } from '../shared/models/integration/integration-rule.interface';
import { IFormIntegration } from '../shared/models/integration/form-integration.interface';
import { FormService } from '../form/form.service';

@Injectable({
  providedIn: 'root',
})
export class QuestionService {
  private isAutoCreate: boolean;
  RULE_KEYS_TO_IMIT = [
    'integrationId',
    'ruleJSON',
    'formVersionId',
    'isAmended',
    'isDeleted',
    'createdAt',
    'updatedAt',
    'userIdCreated',
    'userIdUpdated',
  ];
  integrationService: IntegrationService = inject(IntegrationService);
  formService: FormService = inject(FormService);
  store: Store<AppState> = inject(Store);
  readonly http = inject(HttpClient);
  config: AppConfig = inject(AppConfig);

  getQuestionList(datasetId: number): Observable<QuestionModel[]> {
    return this.http.get<QuestionModel[]>(this.config.buildUri('question') + '/QuestionsByDatasetId/?id=' + datasetId);
  }

  getQuestionListByForm(formId: number): Observable<QuestionModel[]> {
    return this.http.get<QuestionModel[]>(this.config.buildUri('question') + '/QuestionsByFormId/?id=' + formId);
  }

  deleteQuestion(id: number, form = false): Observable<any> {
    const urlPart = form ? 'FormQuestion' : 'Question';
    return this.http.delete<unknown>(this.config.buildUri('question') + '/' + urlPart + '/?id=' + id);
  }

  deleteQuestions(ids: number[]): Observable<any> {
    return this.http.delete(this.config.buildUri('question') + '/DeleteMultipleQuestionsAsync/?ids=' + ids.join(','));
  }

  create(question: QuestionModel, form: FormModel): Observable<QuestionModel> {
    void form;
    delete question.id;
    const urlPart = question.formId ? 'FormQuestion' : 'Question';
    question.organizationId = getState(this.store).user.profile.organizationId;
    question.userIdCreated = getState(this.store).user.profile.userId;

    const payload = { ...question, controls: this.removeDropZonesDeep(question.controls) };
    // payload = this.integrationService.transformQuestion(payload, form);

    return this.http.post<QuestionModel>(this.config.buildUri('question') + '/' + urlPart, payload);
  }

  update(question: QuestionModel, form: FormModel): Observable<QuestionModel> {
    void form;
    const urlPart = question.formId ? 'FormQuestion' : 'Question';
    question.userIdUpdated = getState(this.store).user.profile.userId;
    const payload: QuestionModel = { ...question, controls: this.removeDropZonesDeep(question.controls) };

    // if (form.isAmended) {
    // if its under amendment, we need to get the old version to be able to perform Comparison
    // return this.getFormQuestionsPreviousVersion(form.id).pipe(
    //   switchMap((formInPreviousVersion: FormModel) => {
    //     payload = this.integrationService.transformQuestion(payload, form, formInPreviousVersion);
    //     return this.http.put(this.config.buildUri('question') + '/' + urlPart, payload);
    //   }),
    // );
    // }

    // payload = this.integrationService.transformQuestion(payload, form);
    return this.http.put<QuestionModel>(this.config.buildUri('question') + '/' + urlPart, payload);
  }

  updateMultiple(questions: QuestionModel[]): Observable<QuestionModel[]> {
    // const questionsToUpdate = [];
    // if (form.isAmended) {
    //   // if its under amendment, we need to get the old version to be able to perform Comparison
    //   return this.getFormQuestionsPreviousVersion(form.id).pipe(
    //     switchMap((formInPreviousVersion: FormModel) => {
    //       questions.forEach((q: QuestionModel) => {
    //         questionsToUpdate.push(this.integrationService.transformQuestion(q, form, formInPreviousVersion));
    //       });
    //       return this.http.put(this.config.buildUri('question') + '/UpdateMultipleQuestionsAsync', questionsToUpdate);
    //     }),
    //   );
    // }

    // questions.forEach((q: QuestionModel) => {
    //   questionsToUpdate.push(this.integrationService.transformQuestion(q, form));
    // });
    const payload = questions.map((q: QuestionModel) => {
      return {
        ...q,
        controls: this.removeDropZonesDeep(q.controls),
      };
    });
    return this.http.put<QuestionModel[]>(this.config.buildUri('question') + '/UpdateMultipleQuestionsAsync', payload);
  }

  updateQuestionIntegration(form: FormModel, question: QuestionModel): Observable<IFormIntegration> {
    if (form.isAmended) {
      // if its under amendment, we need to get the old version to be able to perform Comparison
      return this.formService.getFormQuestionsPreviousVersion(form.id).pipe(
        switchMap((formInPreviousVersion: FormModel) => {
          const { integrationJSON, integrationJSONChanges } = this.integrationService.transformQuestion(
            question,
            form,
            formInPreviousVersion,
          );
          const payload = {
            id: question.integrationId,
            isAmended: question.isAmended,
            integrationJSON,
            integrationJSONChanges,
            formVersionId: question.formId,
            formQuestionId: question.id,
          };
          return this.http.put<IFormIntegration>(this.config.buildUri('integration') + '/Integration', payload);
        }),
      );
    }

    const { integrationJSON } = this.integrationService.transformQuestion(question, form);
    return this.http.put<IFormIntegration>(this.config.buildUri('integration') + '/Integration', {
      id: question.integrationId,
      integrationJSON,
      integrationJSONChanges: [],
      formVersionId: question.formId,
      formQuestionId: question.id,
    });
  }

  bulkUpdateQuestionIntegration(form: FormModel, questions: QuestionModel[]) {
    const payload = [];
    if (form.isAmended) {
      // if its under amendment, we need to get the old version to be able to perform Comparison
      return this.formService.getFormQuestionsPreviousVersion(form.id).pipe(
        switchMap((formInPreviousVersion: FormModel) => {
          questions.forEach((q: QuestionModel) => {
            const { integrationJSON, integrationJSONChanges } = this.integrationService.transformQuestion(
              q,
              form,
              formInPreviousVersion,
            );

            payload.push({
              id: q.integrationId,
              isAmended: q.isAmended,
              integrationJSON,
              integrationJSONChanges,
              formVersionId: q.formId,
              formQuestionId: q.id,
            });
          });

          return this.http.put(this.config.buildUri('integration') + '/IntegrationBulkUpdate', payload);
        }),
      );
    }

    questions.forEach((q: QuestionModel) => {
      const { integrationJSON } = this.integrationService.transformQuestion(q, form);
      payload.push({
        id: q.integrationId,
        integrationJSON,
        formVersionId: q.formId,
        formQuestionId: q.id,
      });
    });

    return this.http.put(this.config.buildUri('integration') + '/IntegrationBulkUpdate', payload);
  }

  /**
   * Search question by ID
   * @param id: (string) question ID
   */
  searchById(id: number): Observable<QuestionModel> {
    return this.http.get<QuestionModel>(this.config.buildUri('question') + '/QuestionById/?id=' + id);
  }

  removeDropZonesDeep(controls): any {
    controls = reject(controls, c => c['controlType'] === 'dropzone');
    each(controls, (item, idx) => {
      if (controls[idx].controls) {
        controls[idx].controls = this.removeDropZonesDeep(controls[idx].controls);
      }
    });
    return controls;
  }

  areRulesEqual(rules: IIntegrationRule[], rulesSnapshot: IIntegrationRule[]): boolean {
    const current: IIntegrationRule[] = cloneDeep(rules);
    const snapshot: IIntegrationRule[] = cloneDeep(rulesSnapshot);
    if (snapshot.length !== current.length) {
      return false;
    }
    for (const rule of snapshot) {
      const toFind = current.find(r => r.id === rule.id);
      if (!toFind) {
        return false;
      }
      if (toFind && !this.areSingleRulesEqual(rule, toFind)) {
        return false;
      }
    }
    return true;
  }
  areSingleRulesEqual(rule1: IIntegrationRule, rule2: IIntegrationRule): boolean {
    return isEqual(omit(rule1, this.RULE_KEYS_TO_IMIT), omit(rule2, this.RULE_KEYS_TO_IMIT));
  }

  setQuestionAutoCreateMode(auto: boolean) {
    this.isAutoCreate = auto;
  }

  isQuestionAutoCreateMode() {
    return this.isAutoCreate;
  }

  getQuestionRules(id: number): Observable<IIntegrationRule[]> {
    return this.http.get<IIntegrationRule[]>(this.config.buildUri('integrationRules') + '/IntegrationRules/?questionId=' + id);
  }

  createQuestionRule(payload: IIntegrationRule) {
    return this.http.post(this.config.buildUri('integrationRules') + '/IntegrationRules', payload);
  }
  updateQuestionRule(payload: IIntegrationRule) {
    return this.http.put(this.config.buildUri('integrationRules') + '/IntegrationRules', payload);
  }
  deleteQuestionRule(ruleId: number) {
    return this.http.delete(
      this.config.buildUri('integrationRules') + '/IntegrationRules/?integrationRulesId=' + ruleId,
    );
  }

  bulkDeleteQuestionRules(ruleIds: number[]): Observable<unknown> {
    return this.http.post<unknown>(this.config.buildUri('integrationRules') + '/BulkDelete', ruleIds, {});
  }

  bulkCreateQuestionRules(payload: IIntegrationRule[]) {
    return this.http.post(this.config.buildUri('integrationRules') + '/BulkCreate', payload);
  }

  bulkUpdateQuestionRules(payload: IIntegrationRule[]) {
    return this.http.put(this.config.buildUri('integrationRules') + '/BulkUpdate', payload);
  }
  bulkCreateUpdateQuestionRules(payload: { create: IIntegrationRule[]; update: IIntegrationRule[] }) {
    return this.http.post(this.config.buildUri('integrationRules') + '/BulkCreateUpdate', payload);
  }
}
